import { useState } from "react";
import FieldEditor from "./FieldEditor";
import { Plus, Trash2, Pencil } from "lucide-react";
import Modal from "../Elements/Modal";

interface BackendEditorProps {
  isOpen: boolean;
  onClose: () => void;
  backendData?: Record<string, any>;
  onSave: (data: any) => void;
}

export default function BackendEditor({
  isOpen,
  onClose,
  backendData = {},
  onSave,
}: BackendEditorProps) {
  const [urlToken, setUrlToken] = useState(backendData.url_token || "");
  const [fields, setFields] = useState(backendData.fields || []);
  const [headers, setHeaders] = useState(backendData.headers || []); // 👈 headers añadidos

  const [isFieldEditorOpen, setIsFieldEditorOpen] = useState(false);
  const [fieldEdit, setFieldEdit] = useState<FieldFrm | undefined>(undefined);
  const [typeFrmFieldEdit, setTypeFrmFieldEdit] = useState("Add");

  // Manejo de campos
  const handleSaveField = (updatedField:any) => {
    if (typeFrmFieldEdit === "Add") {
      setFields((prev:any) => [...prev, updatedField]);
    } else {
      setFields((prev:any) =>
        prev.map((f:any) => (f.field_id === updatedField.field_id ? updatedField : f))
      );
    }
  };

  // Manejo de headers
  const addHeader = () => {
    setHeaders((prev:any) => [...prev, { id: crypto.randomUUID(), key: "", value: "" }]);
  };

  const updateHeader = (id:any, key:any, value:any) => {
    setHeaders((prev:any) =>
      prev.map((h:any) => (h.id === id ? { ...h, key, value } : h))
    );
  };

  const removeHeader = (id:any) => {
    setHeaders((prev:any) => prev.filter((h:any) => h.id !== id));
  };

  const handleSaveBackend = () => {
    onSave({ url_token: urlToken, headers, fields });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-[90vw] max-w-5xl overflow-y-auto p-6">
        <h2 className="text-lg font-semibold mb-4">Configuración Backend to Backend</h2>

        {/* URL Token */}
        <label className="block text-sm font-medium">URL de autenticación</label>
        <input
          type="text"
          value={urlToken}
          onChange={(e) => setUrlToken(e.target.value)}
          placeholder="https://api.mi-backend.com/token"
          className="w-full border n-fmr-input px-3 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700 mb-4"
        />

        {/* Headers */}
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium">Encabezados (Headers)</span>
          <button
            type="button"
            onClick={addHeader}
            disabled={headers.length >= 15}
            className={`btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300 ${
              headers.length >= 15
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
          >
            <Plus className="w-4 h-4 m-2" strokeWidth={1.5} />
          </button>
        </div>

        {headers.map((header:any) => (
          <div
            key={header.id}
            className="flex flex-col md:flex-row gap-2 items-start md:items-center mb-2 border rounded-2xl p-3 bg-gray-50"
          >
            <input
              type="text"
              placeholder="Clave"
              value={header.key}
              onChange={(e) => updateHeader(header.id, e.target.value, header.value)}
              className="flex-1 w-full border n-fmr-input px-3 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
            />
            <input
              type="text"
              placeholder="Valor"
              value={header.value}
              onChange={(e) => updateHeader(header.id, header.key, e.target.value)}
              className="flex-1 w-full border n-fmr-input px-3 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
            />
            <button
              type="button"
              onClick={() => removeHeader(header.id)}
              className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300 flex items-center justify-center"
            >
              <Trash2 className="w-4 h-4 text-gray-700" strokeWidth={1.5} />
            </button>
          </div>
        ))}

        {/* Campos del backend */}
        <div className="flex justify-between items-center mt-6 mb-2">
          <span className="font-medium">Campos del backend</span>
          <button
            type="button"
            onClick={() => {
              setFieldEdit({
                field_id: crypto.randomUUID(),
                nameStart: "",
                nameEnd: "",
                label: "",
                type: "text",
                defaultValue: "",
                hidden: false,
                validations: [],
              });;
              setTypeFrmFieldEdit("Add");
              setIsFieldEditorOpen(true);
            }}
            className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
          >
            <Plus className="w-4 h-4 m-2" strokeWidth={1.5} />
          </button>
        </div>

        {fields.map((field:any) => (
          <div
            key={field.field_id}
            className="flex justify-between items-center text-sm border rounded-2xl p-2 bg-gray-50 mb-2"
          >
            <span>
              <span className="font-semibold">{field.label}</span> {field.type}
            </span>
            <div className="flex space-x-2">
              <button
                type="button"
                className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
                onClick={() => {
                  setFieldEdit(field);
                  setTypeFrmFieldEdit("Edit");
                  setIsFieldEditorOpen(true);
                }}
              >
                <Pencil className="w-4 h-4 m-2" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                className="btn-inca-secondary shadow-md rounded-full w-9 h-9 border border-gray-300"
                onClick={() =>
                  setFields(fields.filter((f:any) => f.field_id !== field.field_id))
                }
              >
                <Trash2 className="w-4 h-4 m-2" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}

        {/* Botones de acción */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSaveBackend}
            className="px-4 py-2 rounded-lg bg-blue-700 text-white"
          >
            Guardar
          </button>
        </div>

        {/* Submodal para editar campos */}
        <FieldEditor
          key={isFieldEditorOpen ? JSON.stringify(fieldEdit) : "default"}
          isOpen={isFieldEditorOpen}
          onClose={() => setIsFieldEditorOpen(false)}
          field={fieldEdit}
          onSave={handleSaveField}
          fields={fields}
        />
      </div>
    </Modal>
  );
}
